import { ConfigCatService } from "@torre-labs/feature-flag";
import { forkJoin } from "rxjs";
import { ActionContext, Module } from "vuex";

import { CookieService } from "@/services/CookieService";
import { OrganizationDetails } from "@/services/DTOs/OrganizationDetails";
import { Person } from "@/services/DTOs/Person";
import { OrganizationService } from "@/services/OrganizationService";
import { StorageService } from "@/services/StorageService";
import { UserService } from "@/services/UserService";
import { isTorreEmail } from "@/utils/TesterUtil";
import { Authentication } from ".";

const localeCookieDomain = process.env.VUE_APP_ROOT_DOMAIN || ".torre.ai";
const localeCookieName = "torre.locale";

export class UserStoreState {
  public authentication: Authentication;
  public languagesList: { [key: string]: string };
  public organizations: OrganizationDetails[] | undefined;
  public loadingOrganizations: boolean;

  constructor(
    authentication: Authentication,
    languagesList: { [key: string]: string },
    organizations: OrganizationDetails[] | undefined,
    loadingOrganizations: boolean) {
    this.authentication = authentication;
    this.languagesList = languagesList;
    this.organizations = organizations;
    this.loadingOrganizations = loadingOrganizations;
  }
}

export default {
  namespaced: true,
  state: () => new UserStoreState(new Authentication(null, [], false), {}, [], false),
  mutations: {
    ready(state: UserStoreState, data: { user: Person, services: number[] }) {
      state.authentication = new Authentication(data.user, data.services, true);
    },
    setLanguagesList: (storeState: UserStoreState, languagesList: { [key: string]: string }) => {
      storeState.languagesList = languagesList;
    },
    setOrganizations: (storeState: UserStoreState, organizations: OrganizationDetails[] | undefined) => {
      storeState.organizations = organizations;
    },
    setLoadingOrganizations: (storeState: UserStoreState, loadingOrganizations: boolean) => {
      storeState.loadingOrganizations = loadingOrganizations;
    }
  },
  getters: {
    userIsTorrex: (state: UserStoreState) => {
      if (!!state.authentication.user) {
        return isTorreEmail(state.authentication.user.email);
      } else {
        return false;
      }
    },
    availableLanguages(currentState: UserStoreState): { [key: string]: string } {
      return currentState.languagesList;
    }
  },
  actions: {
    authenticate(context: ActionContext<UserStoreState, any>) {
      forkJoin(
        UserService.getUserData(),
        UserService.getUserServices()
      ).subscribe(
        ([user, services]) => {
          const configCatSdkKey = process.env.VUE_APP_CONFIGCAT_SDK_KEY as string;
          const environmentMode = process.env.VUE_APP_DISCOVERY_ENVIRONMENT_MODE as "development" | "production";
          const configcatService = new ConfigCatService(
            configCatSdkKey,
            environmentMode
          );

          if (user) {
            const data = {
              user,
              configcatService
            };
            context.dispatch("featureFlags/setFlags", data, { root: true });
          }
          if (user.locale) {
            StorageService.setItemLocalStorage("locale", user.locale);
            CookieService.setCookie(localeCookieName, user.locale, 3000, localeCookieDomain);
          }

          context.commit("ready", { user, services });
        },
        (error) => {
          handleGetUserError(context, error);
        }
      );
    },
    refreshUser(context: ActionContext<UserStoreState, any>) {
      forkJoin(
        UserService.getUserData(),
        UserService.getUserServices()
      ).subscribe(
        ([user, services]) => {
          context.commit("ready", { user, services });

          const configCatSdkKey = process.env.VUE_APP_CONFIGCAT_SDK_KEY as string;
          const environmentMode = process.env.VUE_APP_DISCOVERY_ENVIRONMENT_MODE as "development" | "production";
          const configcatService = new ConfigCatService(
            configCatSdkKey,
            environmentMode
          );

          if (user) {
            const data = {
              user,
              configcatService
            };
            context.dispatch("featureFlags/setFlags", data, { root: true });
          }
        },
        (error) => {
          handleGetUserError(context, error);
        }
      );
    },
    getOrganizations(context: ActionContext<UserStoreState, any>) {
      if (context.state.authentication.user && !context.state.loadingOrganizations) {
        context.commit("setLoadingOrganizations", true);
        OrganizationService.getUserAdminRolesOrganizations().subscribe(
          (data) => context.commit("setOrganizations", data as OrganizationDetails[]),
          () => context.commit("setOrganizations", [])
        );
      }
    }
  }
} as Module<UserStoreState, any>;

function handleGetUserError(context: ActionContext<UserStoreState, any>, error: any) {
  if (error && error.status && error.status === 401) {
    context.commit("ready", { user: null, services: [] });
  } else {
    throw error;
  }
}
